import { api, unwrap } from "@lumberset/api-client";
import { accountCreditNote, dateTime, LedgerStatus, money } from "@lumberset/takeoff-ui";
import {
  Button,
  Container,
  EmptyState,
  Field,
  Input,
  Prompt,
  SectionHeader,
  Select,
  Table,
  toast,
} from "@lumberset/ui";
import { Receipt } from "@medusajs/icons";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, useRef, useState } from "react";
import { Crumbs } from "../../components/crumbs";
import { ledgerKindLabel } from "../../components/status";
import { ledgerQuery } from "../../lib/queries";

export const Route = createFileRoute("/_app/ledger")({
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(ledgerQuery),
  component: Ledger,
});

type Entry = Awaited<ReturnType<NonNullable<typeof ledgerQuery.queryFn>>>["entries"][number];

const statusFilters = [
  ["all", "All entries"],
  ["pending", "Pending"],
  ["paid", "Paid"],
  ["failed", "Failed"],
  ["waived", "No charge"],
  ["void", "Void"],
] as const;

type StatusFilter = (typeof statusFilters)[number][0];

const waivable = (entry: Entry) => entry.status === "pending" || entry.status === "failed";

function Ledger() {
  const { data } = useSuspenseQuery(ledgerQuery);
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [waiving, setWaiving] = useState<Entry | null>(null);

  const entries =
    filter === "all" ? data.entries : data.entries.filter((entry) => entry.status === filter);

  return (
    <>
      <Crumbs trail={[{ label: "Ledger" }]} />
      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title="Ledger"
          description="Every charge, credit and refund across all workspaces, newest first. Nothing here is ever edited: a mistake is put right by a new entry or a waiver."
        />
        <div className="px-6 py-3 sm:max-w-xs">
          <Select value={filter} onValueChange={(value) => setFilter(value as StatusFilter)}>
            <Select.Trigger aria-label="Filter by status">
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              {statusFilters.map(([value, label]) => (
                <Select.Item key={value} value={value}>
                  {label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
        </div>

        {entries.length === 0 ? (
          <EmptyState
            icon={<Receipt />}
            title={filter === "all" ? "Nothing in the Ledger yet" : "No entries with that status"}
            description={
              filter === "all"
                ? "An entry appears the moment a Takeoff is released to a workspace."
                : "Try another status."
            }
          />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Date</Table.HeaderCell>
                <Table.HeaderCell>Workspace</Table.HeaderCell>
                <Table.HeaderCell>Description</Table.HeaderCell>
                <Table.HeaderCell>Kind</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Amount</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>
                  <span className="sr-only">Actions</span>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {entries.map((entry) => (
                <Table.Row key={entry.id}>
                  <Table.Cell className="whitespace-nowrap">{dateTime(entry.createdAt)}</Table.Cell>
                  <Table.Cell className="txt-compact-small-plus whitespace-nowrap text-ui-fg-base">
                    {entry.yardName}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="max-w-[360px] truncate" title={entry.description}>
                      {entry.description}
                    </div>
                    {accountCreditNote(entry.accountCreditCents, entry.currency) ? (
                      <div className="txt-compact-xsmall max-w-[360px] truncate text-ui-fg-muted">
                        {accountCreditNote(entry.accountCreditCents, entry.currency)}
                      </div>
                    ) : null}
                    {entry.failureReason ? (
                      <div
                        className="txt-compact-xsmall max-w-[360px] truncate text-ui-fg-muted"
                        title={entry.failureReason}
                      >
                        {entry.failureReason}
                      </div>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    {ledgerKindLabel(entry.kind)}
                  </Table.Cell>
                  <Table.Cell className="tabular whitespace-nowrap text-right text-ui-fg-base">
                    {money(entry.amountCents, entry.currency)}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <LedgerStatus status={entry.status} />
                  </Table.Cell>
                  <Table.Cell className="w-[1%] whitespace-nowrap text-right">
                    {waivable(entry) ? (
                      <Button size="small" variant="secondary" onClick={() => setWaiving(entry)}>
                        Waive
                      </Button>
                    ) : null}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Container>

      <WaivePrompt entry={waiving} onClose={() => setWaiving(null)} />
    </>
  );
}

/** Forgives a fee that has not been collected. The entry stays, marked as no charge, with the reason. */
function WaivePrompt({ entry, onClose }: { entry: Entry | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const reasonRef = useRef<HTMLInputElement>(null);

  const waive = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      unwrap(api.admin.ledger[":id"].waive.$post({ param: { id }, json: { reason } })),
    onSuccess: async ({ entry: waived }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin", "ledger"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "overview"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "yards"] }),
      ]);
      toast.success("Charge waived", { description: waived.description });
      onClose();
    },
    onError: (error) => toast.error(error.message),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!entry) return;
    const reason = String(new FormData(event.currentTarget).get("reason") ?? "").trim();
    waive.mutate({ id: entry.id, reason });
  }

  return (
    <Prompt open={entry !== null} onOpenChange={(open) => !open && onClose()}>
      <Prompt.Content
        onOpenAutoFocus={(event) => {
          // The reason is what this prompt is for, so it gets the focus rather than Cancel.
          event.preventDefault();
          reasonRef.current?.focus();
        }}
      >
        <form onSubmit={submit} key={entry?.id}>
          <Prompt.Header>
            <Prompt.Title>Waive this charge?</Prompt.Title>
            <Prompt.Description>
              {entry
                ? `${entry.yardName} will not pay ${money(entry.amountCents, entry.currency)} for "${entry.description}". The entry stays in the Ledger as no charge, with your reason.`
                : ""}
            </Prompt.Description>
          </Prompt.Header>
          <div className="px-6 pt-5">
            <Field label="Reason" hint="Kept on the entry and in the audit log.">
              {(control) => (
                <Input
                  {...control}
                  ref={reasonRef}
                  name="reason"
                  required
                  minLength={3}
                  maxLength={300}
                  placeholder="Duplicate takeoff, goodwill, agreed with the workspace"
                />
              )}
            </Field>
          </div>
          <Prompt.Footer>
            <Prompt.Cancel>Keep the charge</Prompt.Cancel>
            <Button type="submit" size="small" variant="danger" isLoading={waive.isPending}>
              Waive
            </Button>
          </Prompt.Footer>
        </form>
      </Prompt.Content>
    </Prompt>
  );
}
